import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wind, Volume2, ChevronRight } from 'lucide-react';
import { useFullscreen } from '@/contexts/FullscreenContext';

/**
 * JourneyRespirationPreview - ÉCRAN 7 : Aperçu Player Respiration Premium
 * Montrer la valeur du premium : Voix Hera, Orbe animée, Mode guidé, Interface élégante
 */
const JourneyRespirationPreview = () => {
  const navigate = useNavigate();
  const { setIsFullscreen } = useFullscreen();
  const [showContent, setShowContent] = useState(false);
  const [highlightHera, setHighlightHera] = useState(false);
  
  const isEnglish = localStorage.getItem('i18nextLng')?.includes('en') ||
                    localStorage.getItem('hearen_lang') === 'en';

  useEffect(() => {
    setIsFullscreen(true);
    
    // Fade-in progressif
    setTimeout(() => setShowContent(true), 300);
    
    // Highlight de l'icône Hera après 1.5s
    setTimeout(() => setHighlightHera(true), 1500);
    setTimeout(() => setHighlightHera(false), 2500);
    
    return () => setIsFullscreen(false);
  }, [setIsFullscreen]);

  const handleContinue = () => {
    console.log('💨 JourneyRespirationPreview: Navigating to /journey-therapy');
    navigate('/journey-therapy');
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] pb-32 relative overflow-hidden flex flex-col items-center justify-center px-6">
      {/* Background gradient */}
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(16, 185, 129, 0.3) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      {/* Contenu principal - Fade-in */}
      <div
        className="relative z-10 max-w-md w-full transition-all duration-500"
        style={{
          opacity: showContent ? 1 : 0,
          transform: showContent ? 'translateY(0)' : 'translateY(20px)',
        }}
      >
        {/* Bandeau pédagogique premium */}
        <div 
          className="mb-8 px-6 py-4 rounded-2xl"
          style={{
            background: 'rgba(16, 185, 129, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
          }}
        >
          <div className="flex items-start gap-3">
            <Wind className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-white/90 text-sm leading-relaxed">
                {isEnglish
                  ? 'In Premium, discover your guided exercises with Hera\'s voice.'
                  : 'En Premium, découvrez vos exercices guidés avec la voix Hera.'
                }
              </p>
            </div>
          </div>
        </div>

        {/* Player Respiration Premium (Aperçu) */}
        <div
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 mb-8"
          style={{
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }}
        >
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">
              {isEnglish ? 'Cardiac Coherence' : 'Cohérence Cardiaque'}
            </h2>
            <p className="text-emerald-400 text-sm">
              5-5 • {isEnglish ? 'Guided' : 'Guidé'}
            </p>
          </div>

          {/* Orbe animée - Pulsation douce */}
          <div className="flex justify-center mb-8">
            <div
              className="w-48 h-48 rounded-full flex items-center justify-center relative"
              style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.3) 0%, rgba(5, 150, 105, 0.2) 100%)',
                backdropFilter: 'blur(10px)',
                border: '2px solid rgba(16, 185, 129, 0.4)',
                boxShadow: '0 8px 40px rgba(16, 185, 129, 0.4)',
                animation: 'orb-gentle-pulse-respiration 3s ease-in-out infinite',
              }}
            >
              {/* Cercle intérieur */}
              <div
                className="w-24 h-24 rounded-full"
                style={{
                  background: 'rgba(16, 185, 129, 0.5)',
                  boxShadow: '0 0 20px rgba(16, 185, 129, 0.6)',
                }}
              />
            </div>
          </div>

          {/* Icône Voix Hera - Highlight */}
          <div className="flex justify-center items-center gap-3 mb-6">
            <div
              className="flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-400"
              style={{
                background: highlightHera 
                  ? 'rgba(139, 92, 246, 0.3)' 
                  : 'rgba(255, 255, 255, 0.1)',
                border: highlightHera 
                  ? '2px solid rgba(139, 92, 246, 0.6)' 
                  : '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: highlightHera 
                  ? '0 0 30px rgba(139, 92, 246, 0.5)' 
                  : 'none',
              }}
            >
              <Volume2 
                className="w-5 h-5 text-purple-400"
                style={{
                  filter: highlightHera ? 'drop-shadow(0 0 8px rgba(139, 92, 246, 0.8))' : 'none',
                }}
              />
              <span className="text-white text-sm font-medium">
                {isEnglish ? 'Hera Voice' : 'Voix Hera'}
              </span>
            </div>
          </div>

          {/* Durée figée */}
          <div className="text-center text-white/70 text-sm mb-6">
            1 {isEnglish ? 'minute' : 'minute'}
          </div>

          {/* Sliders désactivés (aperçu) */}
          <div className="space-y-4 opacity-50">
            {/* Volume slider */}
            <div>
              <div className="flex justify-between text-xs text-white/70 mb-2">
                <span>{isEnglish ? 'Volume' : 'Volume'}</span>
                <span>80%</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 rounded-full"
                  style={{ width: '80%' }}
                />
              </div>
            </div>

            {/* Voice volume slider */}
            <div>
              <div className="flex justify-between text-xs text-white/70 mb-2">
                <span>{isEnglish ? 'Voice' : 'Voix'}</span>
                <span>80%</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-purple-500 rounded-full"
                  style={{ width: '80%' }}
                />
              </div>
            </div>
          </div>

          {/* Bouton Play désactivé (preview) */}
          <button
            disabled
            className="w-full mt-8 py-4 rounded-full font-semibold text-white opacity-50 cursor-not-allowed"
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            }}
          >
            {isEnglish ? 'Preview Mode' : 'Mode Aperçu'}
          </button>
        </div>

        {/* CTA Continuer */}
        <div className="flex justify-center">
          <button
            onClick={handleContinue}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-lg transition-all hover:scale-105 active:scale-95 text-white"
            style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              boxShadow: '0 8px 30px rgba(16, 185, 129, 0.4)',
            }}
          >
            {isEnglish ? 'Continue' : 'Continuer'}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Animations CSS */}
      <style>{`
        @keyframes orb-gentle-pulse-respiration {
          0%, 100% { 
            transform: scale(1); 
            opacity: 0.8; 
          }
          50% { 
            transform: scale(1.05); 
            opacity: 1; 
          }
        }
      `}</style>
    </div>
  );
};

export default JourneyRespirationPreview;
